import {useMutation, useQueries} from '@tanstack/react-query';
import {
  AiWorkstreamOutcomesValidationService,
  type WorkstreamOutcomesValidationResponse,
  type WorkstreamOutcomesValidationStatusResponse,
} from 'client';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {getService} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';
import {
  type DraftMetric,
  getMetricOutcomesValidationQueryKey,
  getOutcomeId,
  getOutcomeTitleText,
} from 'views/workstreams/workstreamChecks';

type GeneratingOutcomesValidationTask = {
  taskId: string;
  metricId: string;
  title: string;
  isPreview: boolean;
  saveToMetricId?: string;
};

type GeneratedOutcomesValidationTaskStatus = {
  taskId: string;
  taskStatus: WorkstreamOutcomesValidationStatusResponse | null;
};

const OUTCOMES_VALIDATION_POLL_INTERVAL_MS = 1000;

const getOutcomesValidationPreviewKey = (metricId: string, title: string) =>
  JSON.stringify([metricId, getOutcomeTitleText({titleText: title})]);

const getMetricDataForOutcomesValidation = (validationMetric: DraftMetric) => {
  const metricData = {
    id: getOutcomeId(validationMetric),
    title: getOutcomeTitleText(validationMetric),
  };

  return metricData.id && metricData.title ? metricData : null;
};

export const useMetricOutcomesValidationPreviews = <T extends DraftMetric>({
  enabled,
}: {
  enabled: boolean;
}) => {
  const [previewsByPreviewKey, setPreviewsByPreviewKey] = useState<
    Record<string, WorkstreamOutcomesValidationResponse>
  >({});
  const [generatingTasksById, setGeneratingTasksById] = useState<
    Record<string, GeneratingOutcomesValidationTask>
  >({});
  const [pendingPreviewKeys, setPendingPreviewKeys] = useState<Set<string>>(new Set());
  const handledTaskIdsRef = useRef<Set<string>>(new Set());
  const generatingTasks = useMemo(() => Object.values(generatingTasksById), [generatingTasksById]);

  const {mutateAsync: generateApi} = useMutation({
    mutationFn: (metricData: {id: string; title: string}) =>
      getService(
        AiWorkstreamOutcomesValidationService
      ).previewMetricOutcomesValidationApiV1WorkstreamMetricsOutcomesValidationPreviewPost(
        metricData
      ),
  });

  const getPreviewStatus = useCallback(
    (taskId: string) =>
      getService(
        AiWorkstreamOutcomesValidationService
      ).getMetricOutcomesValidationPreviewStatusApiV1WorkstreamMetricsOutcomesValidationPreviewTaskIdGet(
        taskId
      ),
    []
  );

  const getPreviewSaveToMetricStatus = useCallback(
    (metricId: string, taskId: string) =>
      getService(
        AiWorkstreamOutcomesValidationService
      ).getMetricOutcomesValidationStatusApiV1WorkstreamMetricsWorkstreamMetricIdOutcomesValidationGenerateTaskIdGet(
        Number(metricId),
        taskId
      ),
    []
  );

  const generatingMetricPreviewKeys = useMemo(() => {
    const previewKeys = new Set(pendingPreviewKeys);
    generatingTasks.forEach((task) => {
      previewKeys.add(getOutcomesValidationPreviewKey(task.metricId, task.title));
    });
    return previewKeys;
  }, [generatingTasks, pendingPreviewKeys]);

  const generatedTaskStatusQueries =
    useQueries({
      queries: generatingTasks.map((generatingTask) => ({
        queryKey: [
          'workstream',
          'outcomes_validation',
          generatingTask.taskId,
          generatingTask.saveToMetricId,
        ],
        queryFn: ({signal}: {signal: AbortSignal}) => {
          const request = generatingTask.saveToMetricId
            ? getPreviewSaveToMetricStatus(generatingTask.saveToMetricId, generatingTask.taskId)
            : getPreviewStatus(generatingTask.taskId);
          signal.addEventListener('abort', () => {
            request.cancel();
          });
          return request;
        },
        refetchInterval: OUTCOMES_VALIDATION_POLL_INTERVAL_MS,
        retry: false,
        throwOnError: false,
      })),
    }) ?? [];

  const generatedTaskStatuses = generatedTaskStatusQueries
    .map((result, index) => ({
      taskId: generatingTasks[index]?.taskId,
      taskStatus: result.data ?? null,
    }))
    .filter((status): status is GeneratedOutcomesValidationTaskStatus => Boolean(status.taskId));

  const handleGeneratedTaskStatus = useCallback(
    (
      generatingTask: GeneratingOutcomesValidationTask,
      taskStatus: WorkstreamOutcomesValidationStatusResponse
    ) => {
      if (handledTaskIdsRef.current.has(generatingTask.taskId)) return;
      if (!taskStatus.status || taskStatus.status === 'processing') {
        return;
      }

      handledTaskIdsRef.current.add(generatingTask.taskId);
      setGeneratingTasksById((current) => {
        if (!current[generatingTask.taskId]) {
          return current;
        }

        const next = {...current};
        delete next[generatingTask.taskId];
        return next;
      });

      if (taskStatus.status !== 'completed') return;

      const outcomesValidation = taskStatus.result_json;
      const metricId =
        generatingTask.saveToMetricId ??
        (generatingTask.isPreview
          ? generatingTask.metricId
          : (outcomesValidation?.metric_id ?? generatingTask.metricId));
      const queryKey = getMetricOutcomesValidationQueryKey(metricId);
      if (outcomesValidation) {
        const outcomesValidationToCache: WorkstreamOutcomesValidationResponse = {
          ...outcomesValidation,
          task_id: outcomesValidation.task_id ?? generatingTask.taskId,
          metric_id: metricId,
          metric_title: outcomesValidation.metric_title || generatingTask.title,
        };
        queryClient.setQueryData(queryKey, outcomesValidationToCache);

        if (generatingTask.isPreview) {
          setPreviewsByPreviewKey((current) => ({
            ...current,
            [getOutcomesValidationPreviewKey(metricId, generatingTask.title)]:
              outcomesValidationToCache,
          }));
        }
      }
    },
    []
  );

  useEffect(() => {
    generatedTaskStatuses?.forEach(({taskId, taskStatus}) => {
      if (!taskStatus) return;
      const generatingTask = generatingTasksById[taskId];
      if (!generatingTask) return;
      handleGeneratedTaskStatus(generatingTask, taskStatus);
    });
  }, [generatingTasksById, generatedTaskStatuses, handleGeneratedTaskStatus]);

  const getPreviewKey = useCallback(
    (metric: T) =>
      getOutcomesValidationPreviewKey(getOutcomeId(metric), getOutcomeTitleText(metric)),
    []
  );

  const getPreview = useCallback(
    (metric: T) => previewsByPreviewKey[getPreviewKey(metric)],
    [getPreviewKey, previewsByPreviewKey]
  );

  const isGeneratingPreview = useCallback(
    (metric: T) => generatingMetricPreviewKeys.has(getPreviewKey(metric)),
    [generatingMetricPreviewKeys, getPreviewKey]
  );

  const hasPreviewOrGenerating = useCallback(
    (metric: T) => {
      const previewKey = getPreviewKey(metric);
      return (
        Boolean(previewsByPreviewKey[previewKey]) || generatingMetricPreviewKeys.has(previewKey)
      );
    },
    [generatingMetricPreviewKeys, getPreviewKey, previewsByPreviewKey]
  );

  const clearPreview = useCallback(
    (metric: T) => {
      const previewKey = getPreviewKey(metric);
      setPreviewsByPreviewKey((current) => {
        if (!current[previewKey]) {
          return current;
        }

        const next = {...current};
        delete next[previewKey];
        return next;
      });
    },
    [getPreviewKey]
  );

  const resetPreviews = useCallback(() => {
    setPreviewsByPreviewKey({});
    setPendingPreviewKeys(new Set());
    setGeneratingTasksById((current) => {
      const next = Object.fromEntries(
        Object.entries(current).filter(([, task]) => !task.isPreview)
      );
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, []);

  const startOutcomesValidationGeneration = useCallback(
    async (validationMetric: DraftMetric) => {
      const metricData = getMetricDataForOutcomesValidation(validationMetric);
      if (!metricData) return;

      const previewKey = getOutcomesValidationPreviewKey(metricData.id, metricData.title);
      setPendingPreviewKeys((current) => new Set(current).add(previewKey));

      try {
        const response = await generateApi(metricData);
        handledTaskIdsRef.current.delete(response.task_id);
        setGeneratingTasksById((current) => ({
          ...current,
          [response.task_id]: {
            taskId: response.task_id,
            metricId: metricData.id,
            title: metricData.title,
            isPreview: true,
          },
        }));
      } finally {
        setPendingPreviewKeys((current) => {
          if (!current.has(previewKey)) {
            return current;
          }

          const next = new Set(current);
          next.delete(previewKey);
          return next;
        });
      }
    },
    [generateApi]
  );

  const generatePreview = useCallback(
    (metric: T) => {
      if (!enabled || hasPreviewOrGenerating(metric)) {
        return;
      }

      void startOutcomesValidationGeneration(metric);
    },
    [enabled, hasPreviewOrGenerating, startOutcomesValidationGeneration]
  );

  const savePreviewToMetric = useCallback(
    (metric: T, savedMetricId: number) => {
      const previewKey = getPreviewKey(metric);
      const preview = previewsByPreviewKey[previewKey];
      const savedMetricIdText = savedMetricId.toString();
      if (preview) {
        queryClient.setQueryData(getMetricOutcomesValidationQueryKey(savedMetricIdText), {
          ...preview,
          metric_id: savedMetricIdText,
          metric_title: getOutcomeTitleText(metric) || preview.metric_title,
        });
      }

      const generatingTask = generatingTasks.find(
        (task) =>
          task.isPreview &&
          getOutcomesValidationPreviewKey(task.metricId, task.title) === previewKey
      );
      const taskId = generatingTask?.taskId ?? preview?.task_id;
      if (!taskId) {
        return Promise.resolve();
      }

      const draftMetricId = generatingTask?.metricId ?? preview?.metric_id ?? getOutcomeId(metric);
      handledTaskIdsRef.current.delete(taskId);
      setGeneratingTasksById((current) => ({
        ...current,
        [taskId]: {
          taskId,
          metricId: draftMetricId,
          title:
            getOutcomeTitleText(metric) || preview?.metric_title || generatingTask?.title || '',
          isPreview: false,
          saveToMetricId: savedMetricIdText,
        },
      }));
      return Promise.resolve();
    },
    [generatingTasks, getPreviewKey, previewsByPreviewKey]
  );

  return {
    clearPreview,
    generatePreview,
    getPreview,
    hasPreviewOrGenerating,
    isGeneratingPreview,
    resetPreviews,
    savePreviewToMetric,
  };
};
